import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChartPie } from "lucide-react";
import type { ChartConfig } from "@/components/ui/chart";
import type { DateRange } from "@/hooks/useDateRange";
import { cn } from "@/lib/utils";
import { AnalyticsDonutChartArea } from "../shared/AnalyticsDonutChartArea";
import { RevenueChartCard } from "../revenue/RevenueChartCard";
import {
  REVENUE_CHART_CARD_HEADER_HEIGHT,
  REVENUE_CHART_CARD_PADDING,
  REVENUE_DONUT_CHART_SIZE,
} from "../revenue/constants";
import { formatAnalyticsDateRangeLabel } from "../shared/analyticsFormatting";
import {
  SERVICE_USAGE_CARD_BORDER,
  SERVICE_USAGE_CARD_GAP,
  SERVICE_USAGE_CARD_HEIGHT,
  SERVICE_USAGE_CARD_PADDING_BOTTOM,
  SERVICE_USAGE_CARD_RADIUS,
  SERVICE_USAGE_CARD_WIDTH,
  SERVICES_CHART_CARD_ICON_COLOR,
} from "./constants";
import type { ServiceUsageSegment } from "./mockData";
import { getServiceUsageLegendState } from "./serviceUsageLegend";

// Card height minus: top padding, bottom padding, border, header, header gap,
// donut and the gap between donut and legend.
const LEGEND_MAX_HEIGHT =
  SERVICE_USAGE_CARD_HEIGHT -
  REVENUE_CHART_CARD_PADDING -
  SERVICE_USAGE_CARD_PADDING_BOTTOM -
  2 -
  REVENUE_CHART_CARD_HEADER_HEIGHT -
  12 -
  REVENUE_DONUT_CHART_SIZE -
  SERVICE_USAGE_CARD_GAP;

interface ServiceUsageDonutCardProps {
  segments: ServiceUsageSegment[];
  totalServices: number;
  dateRange: DateRange;
  title?: string;
  centerLabel?: string;
  className?: string;
}

function buildChartConfig(segments: ServiceUsageSegment[]): ChartConfig {
  return segments.reduce<ChartConfig>((config, segment) => {
    config[segment.key] = {
      label: segment.name,
      color: segment.fill,
    };
    return config;
  }, {});
}

function formatDateRangeLabel(dateRange: DateRange): string {
  return formatAnalyticsDateRangeLabel(
    dateRange,
    "Jan 20, 2025 - Jun 09, 2025",
  );
}

function ServiceUsageTooltip({
  active,
  payload,
  total,
  dateRangeLabel,
  totalLabel,
}: {
  active?: boolean;
  payload?: Array<{
    name?: string;
    value?: number;
    payload?: ServiceUsageSegment;
  }>;
  total: number;
  dateRangeLabel: string;
  totalLabel: string;
}) {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0];
  const segment = item.payload;
  const value = Number(item.value ?? 0);
  const percent = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="border-border/50 bg-background grid max-w-[min(20rem,90vw)] min-w-[11rem] gap-2 rounded-lg border px-3 py-2 text-xs shadow-xl">
      <p className="text-muted-foreground">{dateRangeLabel}</p>
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: segment?.fill }}
          />
          <span className="min-w-0 font-medium break-words text-[#171717]">
            {segment?.name}: {percent}% / {value}
          </span>
        </div>
      </div>
      <p className="text-muted-foreground">{totalLabel}</p>
    </div>
  );
}

export const ServiceUsageDonutCard = memo(function ServiceUsageDonutCard({
  segments,
  totalServices,
  dateRange,
  title: titleProp,
  centerLabel: centerLabelProp,
  className,
}: ServiceUsageDonutCardProps) {
  const { t } = useTranslation();
  const title =
    titleProp ?? t("analytics.services.usageTitle", "Service Usage");
  const centerLabel =
    centerLabelProp ?? t("analytics.services.totalServices", "Total Services");
  const tooltipTotalLabel = t(
    "analytics.services.usageTooltipTotal",
    "Total: {{total}}",
    { total: totalServices },
  );

  const legendState = getServiceUsageLegendState(segments, totalServices);
  const emptyText = t(
    "analytics.services.usageEmpty",
    "No services used in the selected period",
  );

  const chartConfig = useMemo(() => buildChartConfig(segments), [segments]);
  const dateRangeLabel = useMemo(
    () => formatDateRangeLabel(dateRange),
    [dateRange],
  );

  const pieAnimationKey = useMemo(
    () =>
      segments.map((segment) => `${segment.key}:${segment.value}`).join("|"),
    [segments],
  );

  const renderTooltip = useMemo(() => {
    function ServiceUsageChartTooltip(tooltipProps: Record<string, unknown>) {
      return (
        <ServiceUsageTooltip
          {...tooltipProps}
          total={totalServices}
          dateRangeLabel={dateRangeLabel}
          totalLabel={tooltipTotalLabel}
        />
      );
    }

    return ServiceUsageChartTooltip;
  }, [dateRangeLabel, tooltipTotalLabel, totalServices]);

  return (
    <RevenueChartCard
      title={title}
      width="full"
      cardWidth={SERVICE_USAGE_CARD_WIDTH}
      icon={<ChartPie />}
      iconColor={SERVICES_CHART_CARD_ICON_COLOR}
      className={cn("shrink-0 !gap-3", className)}
      style={{
        height: SERVICE_USAGE_CARD_HEIGHT,
        minHeight: SERVICE_USAGE_CARD_HEIGHT,
        borderRadius: SERVICE_USAGE_CARD_RADIUS,
        border: `1px solid ${SERVICE_USAGE_CARD_BORDER}`,
        paddingBottom: SERVICE_USAGE_CARD_PADDING_BOTTOM,
      }}
    >
      <div
        className="flex min-h-0 flex-1 flex-col items-center"
        style={{ gap: SERVICE_USAGE_CARD_GAP }}
      >
        <AnalyticsDonutChartArea
          segments={segments}
          total={totalServices}
          chartConfig={chartConfig}
          centerPrimary={totalServices}
          centerSecondary={centerLabel}
          pieAnimationKey={pieAnimationKey}
          animate
          tooltip={renderTooltip}
        />

        {legendState === "empty" ? (
          <p className="w-full text-center text-sm text-[#737373]">
            {emptyText}
          </p>
        ) : (
          <ul
            className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto"
            style={{
              gap: SERVICE_USAGE_CARD_GAP,
              maxHeight: LEGEND_MAX_HEIGHT,
            }}
          >
            {segments.map((segment) => (
              <li
                key={segment.key}
                className="flex items-center justify-between gap-4"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: segment.fill }}
                  />
                  <span
                    className="truncate text-sm leading-normal text-[#171717]"
                    title={segment.name}
                  >
                    {segment.name}
                  </span>
                </div>
                <span className="shrink-0 text-sm text-[#737373]">
                  {segment.value} / {totalServices}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </RevenueChartCard>
  );
});
